import React, { useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";

interface Props {
  /** Button text, e.g. "Re-run statement analysis". */
  label: string;
  /** One line under the button saying what the re-run does and costs. */
  description: string;
  /** Kicks off the re-run; rejections are shown inline. */
  onRerun: () => Promise<unknown>;
  isPending: boolean;
  /** Disable while an analysis is already in flight. */
  disabled?: boolean;
  disabledReason?: string;
}

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object") {
      if ("detail" in data && typeof data.detail === "string") return data.detail;
      if ("error" in data && typeof data.error === "string") return data.error;
      return JSON.stringify(data);
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Footer control for the AI Analysis tabs: re-runs the analysis behind the
 * tab. Sits at the very bottom so it never competes with the result above it.
 */
const RerunAnalysisButton: React.FC<Props> = ({
  label,
  description,
  onRerun,
  isPending,
  disabled = false,
  disabledReason,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const handleClick = async () => {
    setError(null);
    setStarted(false);
    try {
      await onRerun();
      setStarted(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="flex flex-col items-start gap-1 border-t pt-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void handleClick()}
        disabled={isPending || disabled}
        title={disabled ? disabledReason : undefined}
      >
        {isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="mr-2 h-4 w-4" />
        )}
        {isPending ? "Starting…" : label}
      </Button>
      <p className="text-xs text-muted-foreground">
        {disabled && disabledReason ? disabledReason : description}
      </p>
      {started && !error && (
        <p className="text-xs text-muted-foreground">
          Re-run started. Results replace this view when it finishes.
        </p>
      )}
      {error && <p className="text-xs text-destructive">Failed to start re-run: {error}</p>}
    </div>
  );
};

export default RerunAnalysisButton;
